// Admin.jsx
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { isAdminRole } from '../../common/role';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  ShoppingCart, 
  Mail,
  X,
  Menu,
  Wrench
} from 'lucide-react';


function Admin() {
    const { colors } = useTheme();
    const user = useSelector((state) => state?.user?.user);
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        if (!isAdminRole(user?.role)) {
            navigate("/");
        }
    }, [user, navigate]);
    

    const getFullImageUrl = (user) => {
  if (!user) return null;
  
  // Use picturePath instead of profilePic
  const picturePath = user.picturePath;
  
  if (!picturePath) return null;
  
  // If it's already a Cloudinary URL or full URL, return as is
  if (picturePath.startsWith('http')) {
    return picturePath;
  }
  
  // If it's a local upload path, construct the full URL
  const cleanPath = picturePath.replace(/\\/g, '/').replace(/^\/+/, '');
  return `${process.env.REACT_APP_BACKEND_URL}/${cleanPath}`;
};

const profilePicUrl = getFullImageUrl(user);

// Debug logs
console.log("User picturePath:", user?.picturePath);
console.log("Constructed URL:", profilePicUrl);



    const navItems = [
        { path: '/admin/admin-logo', name: 'Logo Management', icon: LayoutDashboard },
        { path: '/admin/admin-banner', name: 'Banner Management', icon: LayoutDashboard },
        { path: '/admin/dashboard', name: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/all-users', name: 'All Users', icon: Users },
        { path: '/admin/all-products', name: 'All Products', icon: Package },
        { path: '/admin/order-inventory', name: 'Orders', icon: ShoppingCart },
        { path: '/admin/admin-tradeins', name: 'TradeIns', icon: ShoppingCart },
        { path: '/admin/admin-refurbished-phones', name: 'Refurbished Phones', icon: ShoppingCart },
        { path: '/admin/admin-giftcards', name: 'Gift Cards', icon: Mail },
        { path: '/admin/admin-deals', name: 'Flash Deals', icon: Mail },
        { path: '/admin/admin-repair', name: 'Repair Requests', icon: Wrench },
        { path: '/admin/newsletter-admin', name: 'Newsletter', icon: Mail },
    ];

    const isActive = (path) => location.pathname === path;

    const handleNavClick = () => {
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    };

    return (
        <div className="flex min-h-[calc(100vh-80px)]" style={{ backgroundColor: colors.bg }}>
            {/* Mobile Overlay */}
            {sidebarOpen && window.innerWidth < 768 && (
                <div 
                    className="fixed inset-0 bg-black/50 z-30 top-20"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside 
                className={`fixed md:static left-0 top-20 mt-[-50px] md:top-auto h-[calc(100vh-80px)] w-64 transition-all duration-300 z-40 flex flex-col ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-64 md:translate-x-0'
                }`}
                style={{
                    backgroundColor: colors.bgSecondary,
                    borderRight: `1px solid ${colors.border}`,
                }}
            >
                {/* Profile Section */}
                <div 
                    className="p-6 border-b flex-shrink-0"
                    style={{ borderColor: colors.border }}
                >
                    <div className="flex items-center space-x-3">
                        {profilePicUrl ? (
                            <img
                                src={profilePicUrl}
                                alt={user.firstName}
                                className="w-12 h-12 rounded-full object-cover transition-transform hover:scale-110 flex-shrink-0"
                                style={{ border: `2px solid ${colors.primary}` }}
                            />
                        ) : (
                            <div 
                                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold transition-transform hover:scale-110 flex-shrink-0"
                                style={{ backgroundColor: colors.primary }}
                            >
                                {user?.firstName?.charAt(0) || 'A'}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p 
                                className="text-sm font-semibold truncate"
                                style={{ color: colors.text }}
                            >
                                {user?.firstName} {user?.lastName}
                            </p>
                            <p 
                                className="text-xs font-medium uppercase tracking-wider truncate"
                                style={{ color: colors.primary }}
                            >
                                {user?.role}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="p-4 space-y-2 overflow-y-auto flex-1">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={handleNavClick}
                                className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 group transform hover:scale-105"
                                style={{
                                    backgroundColor: active ? `${colors.primary}20` : 'transparent',
                                    color: active ? colors.primary : colors.text,
                                }}
                            >
                                <Icon 
                                    className="w-5 h-5 transition-transform group-hover:rotate-12 flex-shrink-0"
                                    style={{ color: active ? colors.primary : colors.textSecondary }}
                                />
                                <span className="font-medium">{item.name}</span>
                                {active && (
                                    <div 
                                        className="ml-auto w-1 h-6 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: colors.primary }}
                                    />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Footer */}
                <div 
                    className="p-4 border-t text-center text-xs flex-shrink-0"
                    style={{ 
                        borderColor: colors.border,
                        color: colors.textSecondary 
                    }}
                >
                    <p>Admin Panel v1.0</p>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 transition-all mt-[-50px] duration-300 flex flex-col overflow-hidden" style={{ backgroundColor: colors.bg }}>
                {/* Mobile Header */}
                <div 
                    className="md:hidden flex items-center justify-between p-4 border-b"
                    style={{
                        borderColor: colors.border,
                        backgroundColor: colors.bgSecondary
                    }}
                >
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-2 rounded-lg transition-all hover:scale-110"
                        style={{
                            backgroundColor: colors.bg,
                            color: colors.primary
                        }}
                    >
                        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                    <h2 className="font-semibold text-lg" style={{ color: colors.text }}>
                        Admin
                    </h2>
                    <div className="w-10" />
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    <div className="p-4 sm:p-6 md:p-8">
                        <Outlet />
                    </div>
                </div>
            </main>

            <style jsx>{`
                /* Custom scrollbar */
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                ::-webkit-scrollbar-track {
                    background: transparent;
                }
                
                ::-webkit-scrollbar-thumb {
                    background: ${colors.border};
                    border-radius: 4px;
                }
                
                ::-webkit-scrollbar-thumb:hover {
                    background: ${colors.textSecondary};
                }

                /* Smooth animations */
                aside {
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                /* Mobile responsiveness */
                @media (max-width: 767px) {
                    aside {
                        position: fixed;
                    }
                }

                @media (min-width: 768px) {
                    aside {
                        position: static;
                        transform: translateX(0) !important;
                    }
                }
            `}</style>
        </div>
    );
}

export default Admin;